import * as React from "react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";

const PRESETS = ["#6E3CFA", "#2DD4BF", "#F59E0B", "#EF4444", "#10B981", "#0EA5E9", "#8B5CF6"];

export default function NewBoardDialog({ open, onOpenChange, companyId, onCreated }) {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [color, setColor] = React.useState("#6E3CFA");
  const [siteId, setSiteId] = React.useState("");
  const [sites, setSites] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    if (!open) { setName(""); setDescription(""); setColor("#6E3CFA"); setSiteId(""); setErr(""); return; }
    api.get(`/companies/${companyId}/sites`).then(({ data }) => {
      setSites(Array.isArray(data?.sites) ? data.sites : (Array.isArray(data) ? data : []));
    }).catch(() => setSites([]));
  }, [open, companyId]);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/boards`, {
        name: name.trim(), description: description.trim(),
        color, site_id: siteId || null,
      });
      toast.success(`Created ${data.name}`);
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not create board");
    } finally { setBusy(false); }
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/30 flex items-start justify-center pt-24 px-4"
         role="dialog" aria-modal="true" onClick={() => onOpenChange(false)}
         data-testid="board-new-dialog">
      <div className="w-full max-w-md bg-card rounded-lg shadow-card border border-brand-rule"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-brand-rule">
          <div className="micro-label">Boards · new</div>
          <div className="mt-0.5 text-[15px] font-semibold text-brand-ink">Create board</div>
        </div>
        <form onSubmit={submit} className="px-5 py-4 space-y-3">
          <div>
            <label className="micro-label">Name</label>
            <input className="field-input mt-1" value={name} onChange={(e) => setName(e.target.value)}
                   maxLength={80} autoFocus required data-testid="board-new-name" />
          </div>
          <div>
            <label className="micro-label">Description</label>
            <input className="field-input mt-1" value={description} onChange={(e) => setDescription(e.target.value)}
                   maxLength={500} data-testid="board-new-desc" />
          </div>
          <div>
            <label className="micro-label">Accent color</label>
            <div className="mt-1 flex items-center gap-2">
              {PRESETS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)}
                        className={`h-6 w-6 rounded-md border-2 ${color === c ? "border-brand-ink" : "border-transparent"}`}
                        style={{ background: c }}
                        data-testid={`board-new-color-${c.replace("#", "")}`} />
              ))}
            </div>
          </div>
          {sites.length > 0 && (
            <div>
              <label className="micro-label">Scope</label>
              <select className="field-input mt-1" value={siteId} onChange={(e) => setSiteId(e.target.value)}
                      data-testid="board-new-site">
                <option value="">Company-wide</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          {err && <div className="text-[12px] text-brand-coral" data-testid="board-new-error">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button type="submit" className="btn-violet flex-1" disabled={busy || !name.trim()}
                    data-testid="board-new-submit">{busy ? "…" : "Create board"}</button>
            <button type="button" onClick={() => onOpenChange(false)} className="btn-outline"
                    data-testid="board-new-cancel">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
